/**
 * people/SupportChampionCard.tsx — "who do I ask for help?" on a colleague's profile.
 *
 * `UR-F10` (Must): "display the department's support champion on a colleague's profile as a CONTACT,
 * with the appointed person's name, and make clear the appointment is the organisation's, not the
 * individual's." That single requirement is three separate obligations, and each one is visible here:
 *
 *   - `PR-F21` — the champion's NAME and AVATAR, near the identity area of the profile.
 *   - `PR-F22` — a plain statement that the ORGANISATION appointed them for the department. The
 *     wording avoids anything that reads as a personal claim, because the subject "cannot change or
 *     hide it" and it is not one of their self-declared items.
 *   - `PR-F26` — a way to CONTACT them. Direct messaging (`CH-F23`) is a phase-2 feature, so the
 *     contact path is their work email, offered as a `mailto:` link. The email is returned by the
 *     backend ONLY for an appointed champion (see `db/publicProfile.ts`), never for a profile subject.
 *
 * `UR-F11` / `PR-F23` — "where a department has NO support champion, show nothing rather than a
 * placeholder". The component returns null for a null champion, so there is no heading, no empty card
 * and no "no champion assigned" note. The same is true when the fetch failed: `fetchSupportChampion`
 * collapses every failure to null precisely so this section can never turn a secondary detail into a
 * visible error on somebody else's profile.
 *
 * `UR-F25` — a champion who has LEFT is never offered as somebody to ask. That is enforced in the
 * data layer (`supportChampionForUserWith` requires the appointed person to be `Active`), not here:
 * a departed champion produces a null and this component renders nothing, exactly as if the
 * department had never had one. There is deliberately no client-side status check to get wrong.
 *
 * NO FIGURES (`PC-F31`): no count of anything, no "champion of N people", no ranking. A champion is an
 * appointment, not a score.
 */
import { AVATAR_UPLOAD_URI_RE, monogram, resolveAvatar } from '../avatar';
import type { SupportChampion } from './api';

/**
 * Scoped styles, injected by the component so it drops into either public-profile page unchanged.
 *
 * Both pages define their own `tb-profile__*` / `tb-userprofile__*` vocabularies, so borrowing either
 * would make the card render differently depending on which page it sat on. It uses the shared design
 * tokens (`--tb-line`, `--tb-muted`, `--tb-accent`) so it still matches both.
 */
const CHAMPION_STYLES = `
  .tb-champion {
    display: flex; align-items: flex-start; gap: 0.85rem;
    border: 1px solid var(--tb-line); border-radius: 8px;
    padding: 0.85rem 1rem; margin: 0;
  }
  .tb-champion__avatar {
    width: 48px; height: 48px; border-radius: 50%; object-fit: cover;
    border: 1px solid var(--tb-line); background: var(--tb-surface); flex: 0 0 auto;
  }
  .tb-champion__avatar--fallback {
    display: flex; align-items: center; justify-content: center;
    font-size: 1.15rem; font-weight: 700;
    color: var(--tb-accent-ink); background: var(--tb-accent);
  }
  .tb-champion__name { font-weight: 600; margin: 0; }
  .tb-champion__appointment { color: var(--tb-muted); font-size: 0.8125rem; margin: 0.15rem 0 0; }
  .tb-champion__contact { margin: 0.4rem 0 0; font-size: 0.9rem; }
  .tb-champion__contact a { color: var(--tb-accent); }
`;

/**
 * An email address only if it plausibly is one, else null.
 *
 * A response is data from the network. The address becomes an `href`, and while React escapes
 * attribute values (so a hostile string cannot break out into markup) a malformed one would render a
 * contact link that silently does nothing — worse than `PR-F23`'s "show nothing", because it looks
 * like a working way to reach somebody. One `@` with something either side, no whitespace, is the
 * whole test: this is a render-path sanity check, not address validation, which belongs to the
 * directory that issued the address.
 */
function safeEmail(email: string | null | undefined): string | null {
  if (typeof email !== 'string') return null;
  const trimmed = email.trim();
  return /^[^\s@]+@[^\s@]+$/.test(trimmed) ? trimmed : null;
}

/**
 * The champion's avatar, through the SAME allowlist chokepoint every other avatar on the site uses.
 *
 * `resolveAvatar` validates the stock token and the uploaded data URI, but passes the directory photo
 * straight through, so the photo is tested here too — it is untrusted TEXT from a cache like any other
 * stored ref. Null means the caller draws a monogram (`PR-F14`: a neutral placeholder, never an empty
 * frame).
 */
function championAvatar(champion: SupportChampion): string | null {
  const override = resolveAvatar(
    { avatarKind: champion.avatarKind, avatarBlobRef: champion.avatarRef },
    null,
  );
  if (override) return override;
  const photo = champion.photoRef;
  return typeof photo === 'string' && AVATAR_UPLOAD_URI_RE.test(photo) ? photo : null;
}

/**
 * The support-champion contact block, or nothing at all.
 *
 * `champion` is null both when the department has no champion and when the lookup could not be made —
 * the two are indistinguishable on purpose, and both mean "render nothing" (`UR-F11`).
 *
 * `headingId` is passed in because the two profile pages own their own heading-id namespaces; the
 * section is labelled by its heading so it is announced as a named region rather than an unlabelled
 * group.
 */
export function SupportChampionCard({
  champion,
  headingId,
}: {
  champion: SupportChampion | null;
  headingId: string;
}) {
  if (!champion) return null;

  const img = championAvatar(champion);
  const email = safeEmail(champion.email);

  return (
    <section aria-labelledby={headingId}>
      <style>{CHAMPION_STYLES}</style>
      {/* "Support champion for <department>" states WHOSE champion this is before the name does, which
          is the PR-F22 point: the appointment is attached to a department, not claimed by a person. */}
      <h2 id={headingId}>Support champion for {champion.department}</h2>
      <div className="tb-champion">
        {img ? (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt -- alt is a name, intentional
          <img className="tb-champion__avatar" src={img} alt={`${champion.name}'s profile photo`} />
        ) : (
          // PR-F14: a neutral placeholder, never an empty frame.
          <span className="tb-champion__avatar tb-champion__avatar--fallback" aria-hidden="true">
            {monogram(champion.name)}
          </span>
        )}
        <div>
          <p className="tb-champion__name">{champion.name}</p>
          {/* PR-F22 in words. "Appointed by the organisation" is the whole point of the sentence —
              without it a marker beside a name reads as something the person claimed for themselves. */}
          <p className="tb-champion__appointment">
            Appointed by the organisation as the support champion for {champion.department}. Not a
            self-declared role.
          </p>
          {/* PR-F26. Omitted rather than shown broken when there is no usable address — an absent
              contact route is honest, a dead link is not. */}
          {email ? (
            <p className="tb-champion__contact">
              <a href={`mailto:${email}`}>Ask {champion.name} for help</a>
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
